import { Link, useLocation } from 'react-router-dom';
import { Logo } from './Logo';
import { resetConsentStatus, resetDntBanner, isDntEnabled } from '../lib/consent';

interface PublicLayoutProps {
  children: React.ReactNode;
  variant?: 'marketing' | 'legal';
  maxWidth?: string;
}

export default function PublicLayout({ children, variant = 'marketing', maxWidth }: PublicLayoutProps) {
  const location = useLocation();
  const isActive = (path: string) => location.pathname === path;

  const navMaxWidth = maxWidth || 'max-w-6xl';

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="border-b border-gray-100">
        <div className={`${navMaxWidth} mx-auto px-4 sm:px-6 lg:px-8`}>
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center">
              <Logo className="h-8 w-auto" />
            </Link>
            {variant === 'marketing' ? (
              <div className="flex items-center gap-6">
                <Link
                  to="/"
                  className={`text-sm transition-colors ${
                    isActive('/')
                      ? 'font-medium text-primary-600'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Home
                </Link>
                <Link
                  to="/directory"
                  className={`text-sm transition-colors ${
                    isActive('/directory')
                      ? 'font-medium text-primary-600'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Public Profiles
                </Link>
                <Link
                  to="/pricing"
                  className={`text-sm transition-colors ${
                    isActive('/pricing')
                      ? 'font-medium text-primary-600'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Pricing
                </Link>
                <Link
                  to="/login"
                  className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
                >
                  Sign in
                </Link>
                <Link to="/signup" className="btn-primary text-sm">
                  Start Free Analysis
                </Link>
              </div>
            ) : (
              <Link
                to="/"
                className="text-sm text-gray-600 hover:text-gray-900"
              >
                Back to Home
              </Link>
            )}
          </div>
        </div>
      </nav>

      {/* Page Content */}
      <main>{children}</main>

      {/* Footer */}
      <footer className="py-12 border-t border-gray-100 bg-gray-50">
        <div className={`${navMaxWidth} mx-auto px-4 sm:px-6 lg:px-8`}>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 mb-8">
            {/* Brand */}
            <div className="col-span-2 md:col-span-1">
              <div className="mb-4">
                <Link to="/" className="inline-flex items-center">
                  <Logo className="h-8 w-auto" loading="lazy" />
                </Link>
              </div>
              <p className="text-sm text-gray-500">SaaS intelligence from your Stripe data.</p>
            </div>

            {/* Product */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-3 text-sm">Product</h4>
              <ul className="space-y-2">
                <li>
                  <Link to="/" className="text-sm text-gray-500 hover:text-gray-700">
                    SaaS Metrics Dashboard
                  </Link>
                </li>
                <li>
                  <Link to="/pricing" className="text-sm text-gray-500 hover:text-gray-700">
                    Pricing Plans
                  </Link>
                </li>
                <li>
                  <Link to="/directory" className="text-sm text-gray-500 hover:text-gray-700">
                    SaaS Company Directory
                  </Link>
                </li>
                <li>
                  <Link to="/security" className="text-sm text-gray-500 hover:text-gray-700">
                    Security
                  </Link>
                </li>
              </ul>
            </div>

            {/* Resources */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-3 text-sm">Resources</h4>
              <ul className="space-y-2">
                <li>
                  <Link to="/saas-metrics" className="text-sm text-gray-500 hover:text-gray-700">
                    SaaS Metrics Guide
                  </Link>
                </li>
                <li>
                  <Link to="/saas-metrics/mrr" className="text-sm text-gray-500 hover:text-gray-700">
                    MRR Calculator
                  </Link>
                </li>
                <li>
                  <Link to="/saas-metrics/churn-rate" className="text-sm text-gray-500 hover:text-gray-700">
                    Churn Rate Guide
                  </Link>
                </li>
                <li>
                  <Link to="/saas-metrics/saas-valuation" className="text-sm text-gray-500 hover:text-gray-700">
                    SaaS Valuation
                  </Link>
                </li>
                <li>
                  <Link to="/saas-metrics/health-score" className="text-sm text-gray-500 hover:text-gray-700">
                    Health Score
                  </Link>
                </li>
              </ul>
            </div>

            {/* Company */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-3 text-sm">Company</h4>
              <ul className="space-y-2">
                <li>
                  <Link to="/login" className="text-sm text-gray-500 hover:text-gray-700">
                    Sign in
                  </Link>
                </li>
                <li>
                  <Link to="/signup" className="text-sm text-gray-500 hover:text-gray-700">
                    Get started
                  </Link>
                </li>
                <li>
                  <a href="mailto:admin@plevits.com" className="text-sm text-gray-500 hover:text-gray-700">
                    Support
                  </a>
                </li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-3 text-sm">Legal</h4>
              <ul className="space-y-2">
                <li>
                  <Link to="/terms" className="text-sm text-gray-500 hover:text-gray-700">
                    Terms of Service
                  </Link>
                </li>
                <li>
                  <Link to="/privacy" className="text-sm text-gray-500 hover:text-gray-700">
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to="/cookies" className="text-sm text-gray-500 hover:text-gray-700">
                    Cookie Policy
                  </Link>
                </li>
                <li>
                  <Link to="/disclaimer" className="text-sm text-gray-500 hover:text-gray-700">
                    Disclaimer
                  </Link>
                </li>
                <li>
                  <button
                    onClick={() => {
                      if (isDntEnabled()) {
                        resetDntBanner();
                      }
                      resetConsentStatus();
                    }}
                    className="text-sm text-gray-500 hover:text-gray-700"
                  >
                    Cookie Preferences
                  </button>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="pt-8 border-t border-gray-200 flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="text-sm text-gray-500">
              &copy; {new Date().getFullYear()} Plevits. Built for SaaS founders.
            </div>
            <div className="flex items-center gap-4 text-sm text-gray-400">
              <span>Read-only Stripe access</span>
              <span>|</span>
              <span>GDPR compliant</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
